// campaign-timing-schedule.ts — the pure timing<->schedule decisions for the
// campaign wizard's Timing step. Extracted from wizard.tsx so the recurrence
// payload contract (FE#240) is unit-tested in node env, the way the rest of the
// campaigns-redesign decisions are.
//
// THE CONTRACT (must match the ORCH#102 relaunch engine)
// ------------------------------------------------------
// The backend `recurring-campaign-orchestrator` re-fires a completed campaign on
// a cadence iff `schedule.isRecurring && schedule.rruleset`, anchoring the next
// occurrence on `schedule.dueDate`. The legacy `recurenceUnit`/`recurrenceQuantity`
// pair is NOT read. So a recurring campaign persists exactly:
//   { isRecurring: true, isTimeValid: true, dueDate: <ISO>, rruleset: <RRULE> }
// Recurrence only applies to a SCHEDULED first send — "start now" has no anchor
// dueDate to compute the next occurrence from — and only when a real rule exists.

import { Frequency } from 'rrule';

import type { Drip } from '@bt-common/schemas/bt/drip';

import type { TimingState } from '@common-reducers/DripEditorReducer';

// FE#240: campaigns offer day/week/month/year recurrence only — sub-daily bulk
// recurrence is nonsensical and ban-risky. Shared by the wizard Timing step and
// the scheduled-campaign edit modal.
export const CAMPAIGN_RECURRENCE_FREQUENCIES: Frequency[] = [
  Frequency.DAILY,
  Frequency.WEEKLY,
  Frequency.MONTHLY,
  Frequency.YEARLY,
];

// BE#657 B1 — a SCHEDULED launch requires a user-picked time. The date picker no
// longer self-seeds "now", so a schedule with an untouched picker carries no
// `scheduledAt`; launching it would persist `isTimeValid:true` with no dueDate and
// the backend would default it to `new Date()` → a silent immediate launch. The
// wizard uses this to gate both leaving the Timing step and the Launch action.
export const scheduledLaunchNeedsTime = (timing: TimingState): boolean =>
  timing.launch === 'schedule' && !timing.scheduledAt;

export const hydrateTiming = (drip: Drip | undefined): TimingState => ({
  launch: drip?.schedule?.isTimeValid ? 'schedule' : 'now',
  scheduledAt: drip?.schedule?.dueDate ? String(drip.schedule.dueDate) : undefined,
  isRecurring: drip?.schedule?.isRecurring ?? false,
  rruleset: drip?.schedule?.rruleset || undefined,
  ...(drip?.randomGap ? { randomGap: drip.randomGap } : {}),
});

// Build the persisted `schedule` from the wizard timing. Scheduling carries the
// chosen dueDate (ISO); "start now" persists no dueDate. Recurrence is emitted
// ONLY for a scheduled send with a live rruleset — matching what the engine reads.
export const buildSchedule = (timing: TimingState): Drip['schedule'] => {
  const scheduling = timing.launch === 'schedule';
  const recurring = scheduling && Boolean(timing.isRecurring && timing.rruleset);
  return {
    isRecurring: recurring,
    isTimeValid: scheduling,
    ...(scheduling && timing.scheduledAt ? { dueDate: timing.scheduledAt } : {}),
    ...(recurring ? { rruleset: timing.rruleset } : {}),
  };
};

// #412 — the persisted drip field for the opt-in randomized gap. `null` (not undefined) when it is
// off: an autosave patch is JSON, which drops undefined, so only an explicit null can CLEAR a gap
// that was saved earlier and then switched off.
export const buildRandomGap = (timing: TimingState): Drip['randomGap'] => timing.randomGap ?? null;
